import axios from 'axios'
import type { VisitLabel } from '../utils/visitLabels'
import { PersonDetails, OwnershipGraphData, CompanyFinancials, PersonFinancialFootprint } from '../types'
import type { EntitySignals, PersonNetwork } from '../types/network'

// Same-origin /api: Vite dev server proxies to PHP (see vite.config.ts and BACKEND_URL).
const API_BASE_URL = '/api'

// Types based on CVR API documentation
export interface Company {
  cvrNummer: string
  navn: string
  adresse?: string
  postdistrikt?: string
  postnummer?: string
  virksomhedsform?: string
  startdato?: string
  telefon?: string
  email?: string
  branche?: string
  ansatte?: number
  /** Composite CVR status (sammensatStatus). */
  status?: string
  /** Search results only: largest current legal owners (max 3). */
  owners?: SearchOwner[]
  /** Search results only: how many more legal owners there are. */
  ownersMore?: number
  /** Search results only: active beneficial owners (max 2). */
  beneficialOwners?: SearchOwner[]
  /** Search results only: journalist flags. */
  flags?: SearchCompanyFlags
}

export interface SearchOwner {
  name: string
  kind: 'company' | 'person'
  cvr: string | null
  enhedsNummer: string | null
  /** ISO code when abroad; 'XX' when foreign but unknown. */
  countryCode: string | null
  percentMin: number | null
  percentMax: number | null
  underFive: boolean
}

export interface SearchCompanyFlags {
  foreignOwner: boolean
  noBeneficialOwner: boolean
  /** Latest legal-owner change (YYYY-MM-DD) when within the last 90 days. */
  ownershipChangedAt: string | null
}

export interface SearchResult {
  companies: Company[]
  totalCount: number
  page: number
  pageSize: number
}

export interface PersonSummary {
  enhedsNummer: number | string | null
  navn: string
  adresse?: string
  isAddressConfidential: boolean
  /** City of the current address; null when protected or not in CVR. */
  by?: string | null
  /** Country of the current address (ISO, upper case); null when protected. */
  landekode?: string | null
  /** The role that best says who this is, current before former. */
  primaryRole?: {
    cvr: string
    company: string
    role: 'Direktør' | 'Ejer' | 'Reel ejer' | 'Bestyrelsesformand' | 'Bestyrelsesmedlem' | 'Deltager'
    current: boolean
  } | null
  activeCompanies?: number
}

export interface PersonSearchResult {
  persons: PersonSummary[]
  totalCount: number
  page: number
  pageSize: number
}

export interface CompanyDetails extends Company {
  // Extended company details
  kreditoplysninger?: any
  reklamebeskyttelse?: any
  deltagere?: any[]
  produktionEnheder?: any[]
  ownership?: any
  /** Current legal subsidiaries (EJERREGISTER, primary list). */
  subsidiaryCount?: number
  /** CVR STIFTER rows (same shape as ownership list entries) */
  founders?: any[]
  /** Ledelse i moderselskab på stiftelsesdato (se `FoundingParentManagementSnapshot` i types) */
  foundingManagementAtParents?: any[]
  deltagerRelation?: any[]
}

// Create axios instance with default config.
// `withCredentials` is required so the browser includes our PHP session
// cookie on every API call — see backend/Auth.php for the cookie config.
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
})

export { apiClient }

/** Graph traversal can chain many CVR lookups — longer than default API calls. */
const GRAPH_REQUEST_TIMEOUT_MS = 60_000
/** Company profile chains founder/parent/subsidiary lookups — allow more time. */
const COMPANY_REQUEST_TIMEOUT_MS = 45_000
/** Person profile can return large affiliation payloads from CVR. */
const PERSON_REQUEST_TIMEOUT_MS = 45_000
const FINANCIAL_REQUEST_TIMEOUT_MS = 60_000
const COMPANY_REQUEST_MAX_RETRIES = 1
const PERSON_REQUEST_MAX_RETRIES = 1
/** Depth for each expand-up / expand-down request (levels from the anchor). */
export const GRAPH_EXPAND_DEPTH = 3

function isAxiosTimeout(error: unknown): boolean {
  return axios.isAxiosError(error) && error.code === 'ECONNABORTED'
}

// Request interceptor for error handling
apiClient.interceptors.request.use(
  (config) => {
    // Add any auth headers here if needed
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor: surface a session-expired event so the React tree
// can redirect to /login. The 401s coming from /auth/* are expected during
// the initial session check and are NOT broadcast — fetchCurrentUser
// handles them locally.
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status
    const url: string = error.config?.url || ''
    if (status === 401 && !url.startsWith('/auth/')) {
      window.dispatchEvent(new CustomEvent('xosa-origin:session-expired'))
    }
    return Promise.reject(error)
  }
)

/**
 * Search for companies by query
 * @param query - Search query (company name, CVR number, or address)
 * @param page - Page number (default: 1)
 * @param pageSize - Results per page (default: 20)
 */
export const searchCompanies = async (
  query: string,
  page: number = 1,
  pageSize: number = 20,
  includeInactive: boolean = false
): Promise<SearchResult> => {
  try {
    const response = await apiClient.get('/search', {
      params: {
        q: query,
        page,
        pageSize,
        ...(includeInactive ? { includeInactive: '1' } : {}),
      },
    })

    const payload = response.data.data
    return {
      companies: payload?.companies ?? [],
      totalCount: typeof payload?.totalCount === 'number' ? payload.totalCount : 0,
      page: typeof payload?.page === 'number' ? payload.page : page,
      pageSize: typeof payload?.pageSize === 'number' ? payload.pageSize : pageSize,
    }
  } catch (error) {
    console.error('Search companies error:', error)
    throw new Error('Failed to search companies')
  }
}

/**
 * Get detailed company information by CVR number
 * @param cvrNumber - Company CVR number
 */
export const getCompanyDetails = async (cvrNumber: string): Promise<CompanyDetails> => {
  let lastError: unknown

  for (let attempt = 0; attempt <= COMPANY_REQUEST_MAX_RETRIES; attempt++) {
    try {
      const response = await apiClient.get(`/company/${cvrNumber}`, {
        timeout: COMPANY_REQUEST_TIMEOUT_MS,
      })
      return response.data.data
    } catch (error) {
      lastError = error
      if (attempt < COMPANY_REQUEST_MAX_RETRIES && isAxiosTimeout(error)) {
        continue
      }
      break
    }
  }

  console.error('Get company details error:', lastError)
  if (isAxiosTimeout(lastError)) {
    throw new Error('Hentning af virksomhed tog for lang tid — prøv igen')
  }
  throw new Error('Kunne ikke hente virksomheden')
}

/**
 * Latest annual-report headline figures (omsætning + resultat) with YoY change.
 */
export const getCompanyFinancials = async (cvrNumber: string): Promise<CompanyFinancials> => {
  try {
    const response = await apiClient.get(`/company/${cvrNumber}/financials`, {
      timeout: FINANCIAL_REQUEST_TIMEOUT_MS,
    })
    return response.data.data
  } catch (error) {
    console.error('Get company financials error:', error)
    throw new Error('Kunne ikke hente regnskabstal')
  }
}

/**
 * Record a profile visit; returns when the user last visited before this
 * session (ISO), or null on a first visit. Failures resolve to null. The
 * label is what »Senest åbnet« on the home page shows for it.
 */
export const touchEntityVisit = async (
  entityType: 'company' | 'person',
  entityId: string,
  label?: VisitLabel
): Promise<string | null> => {
  try {
    const response = await apiClient.post(`/visits/${entityType}/${entityId}`, label ?? {})
    return response.data?.data?.previousVisitAt ?? null
  } catch {
    return null
  }
}

/** A profile the user opened recently, newest first (»Senest åbnet«). */
export interface RecentVisit {
  entityType: 'company' | 'person'
  entityId: string
  /** Null for visits recorded before labels existed. */
  name: string | null
  countryCode: string | null
  detail: string | null
  visitedAt: string
}

export const getRecentVisits = async (limit = 6): Promise<RecentVisit[]> => {
  const response = await apiClient.get('/visits/recent', { params: { limit } })
  return response.data?.data ?? []
}

/** Other companies registered at the same address (same building). */
export interface SameAddressResult {
  /** Formatted address of the company itself. */
  address: string
  /** Floor and door, e.g. »1. tv«; null when the address has neither. */
  unit: string | null
  /** All other companies in the building. */
  totalCount: number
  /** How many of them are not dissolved. */
  activeCount: number
  /** Behind the same floor and door (null without floor/door). */
  unitCount: number | null
  /** Matches for the current filters (for paging). */
  filteredCount: number
  page: number
  pageSize: number
  /** This page, newest companies first. */
  companies: Company[]
}

export interface SameAddressQuery {
  page?: number
  pageSize?: number
  /** Include dissolved companies (default true). */
  includeDissolved?: boolean
  /** Only the same floor and door. */
  sameUnit?: boolean
  /** Name filter. */
  q?: string
}

export const getCompaniesAtSameAddress = async (
  cvrNumber: string,
  query: SameAddressQuery = {}
): Promise<SameAddressResult> => {
  try {
    const response = await apiClient.get(`/company/${cvrNumber}/same-address`, {
      params: {
        page: query.page ?? 1,
        pageSize: query.pageSize ?? 50,
        inactive: query.includeDissolved === false ? '0' : '1',
        unit: query.sameUnit ? '1' : '0',
        q: query.q || undefined,
      },
    })
    return response.data.data
  } catch (error) {
    console.error('Get same-address companies error:', error)
    throw new Error('Kunne ikke hente selskaber på samme adresse')
  }
}

export interface PersonLinkedCompany {
  cvrNummer: string
  navn: string
  virksomhedsform: string
  status: string
  countryCode: string | null
  persons: {
    enhedsNummer: string
    navn: string
    /** Roles in the company you are looking at. */
    rolesHere: string[]
    /** Roles in this other company. */
    rolesThere: string[]
  }[]
}

export interface CompanyPersonConnections {
  /** Ranked by number of shared people. */
  companies: PersonLinkedCompany[]
  personsChecked: number
  personsTotal: number
  /** More people than were looked up. */
  truncated: boolean
}

/** Other active companies that this company's people are also involved in. */
export const getCompanyPersonConnections = async (
  cvrNumber: string
): Promise<CompanyPersonConnections> => {
  try {
    const response = await apiClient.get(`/company/${cvrNumber}/person-connections`, {
      timeout: GRAPH_REQUEST_TIMEOUT_MS,
    })
    return response.data.data
  } catch (error) {
    console.error('Get company person connections error:', error)
    throw new Error('Kunne ikke hente forbindelser via personer')
  }
}

/**
 * Search for persons by name
 */
export const searchPersons = async (
  query: string,
  page: number = 1,
  pageSize: number = 20
): Promise<PersonSearchResult> => {
  try {
    const response = await apiClient.get('/search/persons', {
      params: { q: query, page, pageSize },
    })
    const payload = response.data.data
    return {
      persons: payload?.persons ?? [],
      totalCount: typeof payload?.totalCount === 'number' ? payload.totalCount : 0,
      page: typeof payload?.page === 'number' ? payload.page : page,
      pageSize: typeof payload?.pageSize === 'number' ? payload.pageSize : pageSize,
    }
  } catch (error) {
    console.error('Search persons error:', error)
    throw new Error('Failed to search persons')
  }
}

/**
 * Search companies by address
 * @param address - Address to search for
 * @param page - Page number (default: 1)
 * @param pageSize - Results per page (default: 20)
 */
export const searchCompaniesByAddress = async (
  address: string,
  page: number = 1,
  pageSize: number = 20
): Promise<Company[]> => {
  try {
    const response = await apiClient.get('/search/address', {
      params: {
        address,
        page,
        pageSize,
      },
    })

    return response.data.companies || []
  } catch (error) {
    console.error('Search by address error:', error)
    throw new Error('Failed to search companies by address')
  }
}

/**
 * Get company production units
 * @param cvrNumber - Company CVR number
 */
export const getCompanyProductionUnits = async (cvrNumber: string): Promise<any[]> => {
  try {
    const response = await apiClient.get(`/company/${cvrNumber}/production-units`)
    return response.data.productionUnits || []
  } catch (error) {
    console.error('Get production units error:', error)
    throw new Error('Failed to get production units')
  }
}

/**
 * Get company participants/representatives
 * @param cvrNumber - Company CVR number
 */
export const getCompanyParticipants = async (cvrNumber: string): Promise<any[]> => {
  try {
    const response = await apiClient.get(`/company/${cvrNumber}/participants`)
    return response.data.participants || []
  } catch (error) {
    console.error('Get participants error:', error)
    throw new Error('Failed to get company participants')
  }
}

/**
 * Get person details by enhedsNummer
 * @param enhedsNummer - Person entity number
 */
export const getPersonDetails = async (enhedsNummer: string): Promise<PersonDetails> => {
  let lastError: unknown

  for (let attempt = 0; attempt <= PERSON_REQUEST_MAX_RETRIES; attempt++) {
    try {
      const response = await apiClient.get(`/person/${enhedsNummer}`, {
        timeout: PERSON_REQUEST_TIMEOUT_MS,
      })
      return response.data.data
    } catch (error) {
      lastError = error
      if (attempt < PERSON_REQUEST_MAX_RETRIES && isAxiosTimeout(error)) {
        continue
      }
      break
    }
  }

  console.error('Get person details error:', lastError)
  if (isAxiosTimeout(lastError)) {
    throw new Error('Hentning af person tog for lang tid — prøv igen')
  }
  throw new Error('Kunne ikke hente personoplysninger')
}

/**
 * A person's business network for the Forbindelser tab: ranked connections
 * with history, holding look-through and patterns. Slow on first load (one
 * lookup per company), so only fetched when the tab is opened.
 */
export const getPersonNetwork = async (enhedsNummer: string): Promise<PersonNetwork> => {
  try {
    const response = await apiClient.get(`/person/${enhedsNummer}/network`, {
      timeout: 120_000,
    })
    return response.data.data
  } catch (error: any) {
    if (error?.code === 'ECONNABORTED') {
      throw new Error('Netværket tog for lang tid at beregne. Prøv igen om lidt.')
    }
    throw new Error('Kunne ikke hente netværket.')
  }
}

/** Hits for the entity's name in registers outside CVR (PEP, sanctions, Folketinget, Offshore Leaks). */
export const getEntitySignals = async (
  type: 'person' | 'company',
  id: string
): Promise<EntitySignals> => {
  const response = await apiClient.get(`/${type}/${id}/signals`, { timeout: 30_000 })
  return response.data.data
}

/**
 * Aggregated revenue scale for ejerskab vs. indflydelse (all active affiliations).
 */
export const getPersonFinancialFootprint = async (
  enhedsNummer: string,
): Promise<PersonFinancialFootprint> => {
  try {
    const response = await apiClient.get(`/person/${enhedsNummer}/financial-footprint`, {
      timeout: 120_000,
    })
    return response.data.data
  } catch (error) {
    console.error('Get person financial footprint error:', error)
    throw new Error('Kunne ikke beregne økonomisk omfang')
  }
}

/**
 * Get the ownership graph for a CVR number.
 * Returns flat {rootCvr, nodes, edges} — see OwnershipGraphData.
 */
export const getOwnershipGraph = async (cvrNumber: string): Promise<OwnershipGraphData> => {
  try {
    const response = await apiClient.get(`/company/${cvrNumber}/graph`, {
      timeout: GRAPH_REQUEST_TIMEOUT_MS,
    })
    return response.data.data
  } catch (error) {
    console.error('Get ownership graph error:', error)
    throw new Error('Failed to get ownership graph')
  }
}

/** Owners above the company as they were on `asOf` (YYYY-MM-DD). Subsidiaries are not included. */
export const getOwnershipGraphAsOf = async (
  cvrNumber: string,
  asOf: string
): Promise<OwnershipGraphData> => {
  try {
    const response = await apiClient.get(`/company/${cvrNumber}/graph`, {
      params: { asOf },
      timeout: GRAPH_REQUEST_TIMEOUT_MS,
    })
    return response.data.data
  } catch (error) {
    console.error('Get ownership graph as of date error:', error)
    throw new Error('Kunne ikke hente ejerskabet på datoen')
  }
}

export interface OwnershipTimelineEvent {
  date: string
  kind: 'in' | 'out' | 'change'
  name: string
  countryCode: string | null
  beneficial: boolean
}

/** Owners in, out and stake changes for the time slider, oldest first. */
export const getOwnershipTimeline = async (cvrNumber: string): Promise<OwnershipTimelineEvent[]> => {
  try {
    const response = await apiClient.get(`/company/${cvrNumber}/ownership-timeline`)
    return response.data.data?.events ?? []
  } catch (error) {
    console.error('Get ownership timeline error:', error)
    throw new Error('Kunne ikke hente ejerskabets tidslinje')
  }
}

export interface ConnectionNode {
  /** »person:<enhedsnr>« or »cvr:<cvr>« (foreign participants: »foreign:…«). */
  id: string
  name: string
  kind: 'person' | 'company' | 'foreign-company'
}

export interface ConnectionEdge {
  /** Participant (owner, board member …) */
  from: string
  /** Company */
  to: string
  kind: 'ownership' | 'role'
  /** Verb phrase read as »<from> <label> <to>«, e.g. »ejer 50–66,66 % af«; past tense when ended. */
  label: string
  /** False for a role that has ended (or a founding); missing on old cached results. */
  current?: boolean
  /** »2023–2024« for an ended role. */
  period?: string | null
}

export interface ConnectionPath {
  nodes: ConnectionNode[]
  edges: ConnectionEdge[]
}

export interface ConnectionResult {
  paths: ConnectionPath[]
  /** The search hit its lookup limit; a longer connection may exist. */
  truncated: boolean
}

/**
 * »Find forbindelse«: shortest connections through current ownership and
 * roles — with `history`, through former roles and founders too.
 */
export const findConnections = async (
  from: string,
  to: string,
  mode: 'all' | 'ownership' = 'all',
  maxLinks = 5,
  history = false
): Promise<ConnectionResult> => {
  try {
    const response = await apiClient.get('/connections', {
      params: { from, to, mode, maxLinks, ...(history ? { history: 1 } : {}) },
      timeout: GRAPH_REQUEST_TIMEOUT_MS,
    })
    return response.data.data
  } catch (error) {
    console.error('Find connections error:', error)
    throw new Error('Kunne ikke finde forbindelser')
  }
}

/** Downward expansion fragment — merge client-side with the upward graph. */
export const getOwnershipGraphDownstream = async (
  cvrNumber: string,
  nodesUsed: number,
  fromCvr?: string,
): Promise<OwnershipGraphData> => {
  try {
    const response = await apiClient.get(`/company/${cvrNumber}/graph`, {
      params: {
        expand: 'down',
        nodesUsed,
        depth: GRAPH_EXPAND_DEPTH,
        ...(fromCvr ? { fromCvr } : {}),
      },
      timeout: GRAPH_REQUEST_TIMEOUT_MS,
    })
    return response.data.data
  } catch (error) {
    console.error('Get ownership graph downstream error:', error)
    throw new Error('Failed to get downstream ownership graph')
  }
}

/** Upward expansion from a truncated company — merge client-side. */
export const getOwnershipGraphUpstream = async (
  profileCvr: string,
  fromCvr: string,
  nodesUsed: number,
): Promise<OwnershipGraphData> => {
  try {
    const response = await apiClient.get(`/company/${profileCvr}/graph`, {
      params: { expand: 'up', fromCvr, nodesUsed, depth: GRAPH_EXPAND_DEPTH },
      timeout: GRAPH_REQUEST_TIMEOUT_MS,
    })
    return response.data.data
  } catch (error) {
    console.error('Get ownership graph upstream error:', error)
    throw new Error('Failed to get upstream ownership graph')
  }
}

export default {
  searchCompanies,
  searchPersons,
  getCompanyDetails,
  getOwnershipGraph,
  getOwnershipGraphDownstream,
  getOwnershipGraphUpstream,
  searchCompaniesByAddress,
  getCompanyProductionUnits,
  getCompanyParticipants,
  getPersonDetails,
  getPersonNetwork,
  getEntitySignals,
}
